import React, { useEffect, useState } from 'react';
import {
  Container,
  TextInput,
  Select,
  MultiSelect,
  Button,
  Group,
  Stack,
  Loader,
} from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';
import { hours, monthDays, weekDays } from '../utils/BillingUtil.ts';
import useParkingBillingSetting from '../hooks/useParkingBillingSetting.ts';
import useSiteProviderStores from '../hooks/useSiteProviderStores.ts';
import useSiteProviderAccountants from '../hooks/useSiteProviderAccountants.ts';
import { SiteProvider } from '../models/siteProvider.ts';
import useUpdateBillingSetting from '../hooks/useUpdateParkingBillingSetting.ts';
import { useForm } from '@mantine/form';
import { createParkingBillingSettingRequest } from '../api/requests/parking-billing-setting-request.ts';
import { notifications } from '@mantine/notifications';
import useSiteProviders from '../hooks/useSiteProviders.ts';
import useCreateParkingBillingSetting from '../hooks/useCreateParkingBillingSetting.ts';
import { convertToReportPeriodType } from '../models/parkingBillingSetting.ts';

export type ParkingBillingSettingForm = {
  name: string;
  siteProvider: SiteProvider;
  executionType: string;
  reportPeriodType: string;
  triggerDay: string;
  triggerTime: string;
  storeIds: string[];
  accountantIds: string[];
  tags: string[];
};

import { PeriodOptionDescriptions } from './BillingSettingsPageModel';

export default function BillingSettingsPage() {
  const { id } = useParams();
  const isEdit = id !== undefined;
  const navigate = useNavigate();
  const { isLoading: isDateLoading, data } = useParkingBillingSetting(id);
  const { mutate: updateSetting, isPending: isUpdateLoading } =
    useUpdateBillingSetting();
  const { mutate: createSetting, isPending: isCreateLoading } =
    useCreateParkingBillingSetting();

  const form = useForm<ParkingBillingSettingForm>({
    mode: 'controlled',
    initialValues: {
      name: '',
      siteProvider: { id: '', name: '' },
      executionType: 'month',
      reportPeriodType: 'month',
      triggerDay: '1',
      triggerTime: '9',
      storeIds: [],
      accountantIds: [],
      tags: [],
    },
  });

  const [siteProviderId, setSiteProviderId] = useState<string>(
    form.getValues().siteProvider.id,
  );

  form.watch('siteProvider.id', ({ previousValue, value }) => {
    if (previousValue !== '') {
      form.setValues({
        storeIds: [],
        accountantIds: [],
      });
    }
    setSiteProviderId(value);
  });

  const { data: siteProviders } = useSiteProviders();
  const { isLoading: isStoresLoading, data: stores } =
    useSiteProviderStores(siteProviderId);
  const { isLoading: isAccountantsLoading, data: accountants } =
    useSiteProviderAccountants(siteProviderId);

  const isFormReady =
    !isDateLoading && !isStoresLoading && !isAccountantsLoading;

  const tagOptions = [
    { value: '5 號匯款', label: '5 號匯款' },
    { value: '15 號匯款', label: '15 號匯款' },
    { value: '25 號匯款', label: '25 號匯款' },
    { value: '週四匯款', label: '週四匯款' },
    { value: '週五匯款', label: '週五匯款' },
    { value: '停車場 - 月結', label: '停車場 - 月結' },
    { value: '停車場 - 半月結', label: '停車場 - 半月結' },
    { value: '北市委外 - 週結', label: '北市委外 - 週結' },
    { value: '北市委外 - 月結', label: '北市委外 - 月結' },
  ];

  const periodOptions = [
    { value: 'week', label: '每週' },
    { value: 'month', label: '每月' },
    { value: 'firstHalfOfMonth', label: '上半月' },
    { value: 'secondHalfOfMonth', label: '下半月' },
  ];

  const triggerPeriodOptions = [
    { value: 'week', label: '每週' },
    { value: 'month', label: '每月' },
  ];

  useEffect(() => {
    if (data && isFormReady) {
      const reportPeriodType = convertToReportPeriodType(
        data.args.reportPeriod,
      );
      form.setValues({
        name: data.name,
        siteProvider: data.siteProvider,
        executionType:
          data.scheduledTaskTriggerRule.settings.monthday === '*'
            ? 'week'
            : 'month',
        reportPeriodType: reportPeriodType,
        triggerDay:
          data.scheduledTaskTriggerRule.settings.monthday === '*'
            ? data.scheduledTaskTriggerRule.settings.weekday
            : data.scheduledTaskTriggerRule.settings.monthday,
        triggerTime: data.scheduledTaskTriggerRule.settings.hour,
        storeIds: data.args.storeIds,
        accountantIds: data.args.accountantIds,
        tags: data.args.billingTags || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFormReady]);

  useEffect(() => {
    if (accountants) {
      const existIds = accountants.map((x) => x.id);
      form.setValues({
        accountantIds: form
          .getValues()
          .accountantIds.filter((x) => existIds.includes(x)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountants]);

  const handleSubmit = (values: ParkingBillingSettingForm) => {
    const request = createParkingBillingSettingRequest(values);
    if (isEdit) {
      updateSetting(
        {
          id: data.id,
          request: request,
        },
        {
          onSuccess: () => {
            notifications.show({
              title: '成功',
              message: '對帳設定已更新',
              color: 'green',
            });
          },
        },
      );
    } else {
      createSetting(request, {
        onSuccess: () => {
          notifications.show({
            title: '成功',
            message: '對帳設定已新增',
            color: 'green',
          });
        },
      });
    }
  };

  {
    if (!isFormReady) {
      return <Loader />;
    }
  }

  return (
    <Container fluid px="md" py="md">
      <form
        onSubmit={form.onSubmit((values) => {
          handleSubmit(values);
        })}
      >
        <Stack style={{ maxWidth: 640 }}>
          <Select
            label="廠商"
            placeholder="選擇廠商"
            data={siteProviders?.map((siteProvider) => {
              return {
                value: siteProvider.id,
                label: siteProvider.name,
              };
            })}
            key={form.key('siteProvider.id')}
            {...form.getInputProps('siteProvider.id')}
            disabled={isEdit === true}
            required
            searchable
          />
          <TextInput
            label="設定名稱"
            placeholder="輸入設定名稱"
            key={form.key('name')}
            {...form.getInputProps('name')}
            required
          />
          <MultiSelect
            label="指定停車場"
            placeholder="選擇停車場"
            data={stores?.map((store) => ({
              value: store.id,
              label: store.name,
            }))}
            key={form.key('storeIds')}
            {...form.getInputProps('storeIds')}
            clearable
            searchable
          />
          <MultiSelect
            label="報表寄件對象"
            placeholder="輸入或選擇 Email"
            data={accountants?.map((accountant) => ({
              value: accountant.id,
              label: accountant.email,
            }))}
            key={form.key('accountantIds')}
            {...form.getInputProps('accountantIds')}
            clearable
            searchable
          />
          <MultiSelect
            label="標籤"
            placeholder="選擇標籤"
            data={tagOptions}
            key={form.key('tags')}
            {...form.getInputProps('tags')}
            clearable
          />
          <Select
            label="報表週期"
            placeholder="選擇週期"
            data={periodOptions}
            description={
              PeriodOptionDescriptions[form.getValues().reportPeriodType]
            }
            key={form.key('reportPeriodType')}
            {...form.getInputProps('reportPeriodType')}
            required
          />
          <Select
            label="執行週期"
            placeholder="選擇執行頻率"
            data={triggerPeriodOptions}
            key={form.key('executionType')}
            {...form.getInputProps('executionType')}
            required
          />
          {form.getValues().executionType === 'week' && (
            <Group>
              <Select
                label="星期"
                placeholder="選擇星期"
                data={weekDays}
                key={form.key('triggerDay')}
                {...form.getInputProps('triggerDay')}
                required
              />
              <Select
                label="時間"
                placeholder="選擇時間"
                data={hours}
                key={form.key('triggerTime')}
                {...form.getInputProps('triggerTime')}
                required
              />
            </Group>
          )}
          {form.getValues().executionType === 'month' && (
            <Group>
              <Select
                label="日期"
                placeholder="選擇日期"
                data={monthDays}
                key={form.key('triggerDay')}
                {...form.getInputProps('triggerDay')}
                required
              />
              <Select
                label="時間"
                placeholder="選擇時間"
                data={hours}
                key={form.key('triggerTime')}
                {...form.getInputProps('triggerTime')}
                required
              />
            </Group>
          )}
          <Group mt="md">
            <Button
              variant="outline"
              onClick={() => navigate('/billing/settings')}
            >
              返回
            </Button>
            <Button type="submit" loading={isCreateLoading || isUpdateLoading}>
              {isEdit ? '更新' : '新增'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}
